import type { MarketVariants } from '@/lib/i18n'

export type Slide5SarahCopy = {
  /** 2FA diagram: password field label and the code SMS on the phone mockup. */
  passwordLabel: string
  smsCode: string

  title: string
  body: string
  note: string
  guideButton: string

  /** Setup guide slideover. */
  guideTitle: string
  closeAria: string
  step1Heading: string
  /** Step 1 body split around the inline cog icon: "{before}[icon]{after}". */
  step1Before: string
  step1After: string
  step2Heading: string
  step2Body: string
  step3Heading: string
  step3Body: string
  emailHeading: string
  emailBody: string
  gmailName: string
  gmailDesc: string
  outlookName: string
  outlookDesc: string
  yahooName: string
  yahooDesc: string
  icloudName: string
  icloudDesc: string
  /** AOL row is hidden on the Qatar face (US-only provider). */
  aolName: string
  aolDesc: string
  thenBanks: string
}

export const copy: MarketVariants<Slide5SarahCopy> = {
  US: {
    passwordLabel: 'Password',
    smsCode: 'Your code is 428163.',

    title: 'Turn on two-factor authentication (2FA).',
    body:
      '2FA adds a second layer of protection to your online accounts. Even if a scammer steals your password, they can’t get in without a second code.',
    note:
      'It takes two minutes to set up, and it’s the single best thing you can do to protect your accounts.',
    guideButton: 'How to turn it on',

    guideTitle: 'How to turn on two-step verification',
    closeAria: 'Close',
    step1Heading: 'Open your account settings',
    step1Before: 'Look for the settings menu, usually marked with a cog icon ',
    step1After: ', then find “Security” or “Sign-in.”',
    step2Heading: 'Find “two-step verification”',
    step2Body:
      'It may also be called “2FA” or “two-factor authentication.” Turn it on, then choose text message or an authenticator app.',
    step3Heading: 'Enter the code they send you',
    step3Body:
      'A code will arrive by text or appear in your app. Type it in to confirm. That’s it.',
    emailHeading: 'Start with your email',
    emailBody:
      'Your email is the key to your other accounts. If someone gets in, they can reset the password on everything else.',
    gmailName: 'Gmail',
    gmailDesc: 'Go straight to 2-step verification.',
    outlookName: 'Outlook or Hotmail',
    outlookDesc: 'Open Microsoft security settings.',
    yahooName: 'Yahoo Mail',
    yahooDesc: 'Open account security.',
    icloudName: 'iCloud',
    icloudDesc: 'Sign in, then open “Sign-In and Security.”',
    aolName: 'AOL Mail',
    aolDesc: 'Open account security.',
    thenBanks: 'Then your bank. Then your main social media.',
  },
  ar: {
    passwordLabel: 'كلمة المرور',
    smsCode: 'رمز التحقق الخاص بك هو 428163.',

    title: 'فعّل التحقق بخطوتين (2FA).',
    body:
      'يضيف التحقق بخطوتين طبقة حماية ثانية لحساباتك على الإنترنت. فحتى لو سرق محتال كلمة مرورك، لن يستطيع الدخول من دون الرمز الثاني.',
    note:
      'يستغرق تفعيله دقيقتين، وهو أفضل خطوة واحدة يمكنك اتخاذها لحماية حساباتك.',
    guideButton: 'كيف تفعّله',

    guideTitle: 'كيفية تفعيل التحقق بخطوتين',
    closeAria: 'إغلاق',
    step1Heading: 'افتح إعدادات حسابك',
    step1Before: 'ابحث عن قائمة الإعدادات، وعادةً ما تحمل رمز الترس ',
    step1After: '، ثم ابحث عن "الأمان" أو "تسجيل الدخول".',
    step2Heading: 'ابحث عن "التحقق بخطوتين"',
    step2Body:
      'قد يُسمّى أيضاً "2FA" أو "المصادقة الثنائية". فعّله، ثم اختر الرسائل النصية أو تطبيق المصادقة.',
    step3Heading: 'أدخل الرمز الذي يصلك',
    step3Body:
      'سيصلك رمز برسالة نصية أو يظهر في التطبيق. أدخله للتأكيد. وهذا كل شيء.',
    emailHeading: 'ابدأ ببريدك الإلكتروني',
    emailBody:
      'بريدك الإلكتروني هو مفتاح حساباتك الأخرى. إذا دخله أحد، فيمكنه إعادة تعيين كلمة المرور لكل شيء آخر.',
    gmailName: 'Gmail',
    gmailDesc: 'انتقل مباشرة إلى التحقق بخطوتين.',
    outlookName: 'Outlook أو Hotmail',
    outlookDesc: 'افتح إعدادات الأمان في مايكروسوفت.',
    yahooName: 'Yahoo Mail',
    yahooDesc: 'افتح أمان الحساب.',
    icloudName: 'iCloud',
    icloudDesc: 'سجّل الدخول، ثم افتح "تسجيل الدخول والأمان".',
    aolName: 'AOL Mail',
    aolDesc: 'افتح أمان الحساب.',
    thenBanks: 'ثم البنك. ثم حساباتك الرئيسية على وسائل التواصل.',
  },
}
